import * as FileSaver from 'file-saver';
import ExcelJS from 'exceljs';
import XLSX from 'sheetjs-style-v2';
import { Formatter } from 'utils';
import type { ViagemBilling } from '../../types';
import { formatViagemCurrency, parseViagemCurrencyValue } from '../../utils';
import cortevaLogo from 'assets/images/corteva-logo.png';

export type ViagensBillingExportType = 'txt' | 'xlsx' | 'billingCover';

const formatDate = (value?: string | null) => {
  return value ? Formatter.date(value, { format: 'dd/MM/yyyy HH:mm' }) : '';
};

const formatDateOnly = (value?: string | null) => {
  return value ? Formatter.date(value, { format: 'dd/MM/yyyy' }) : '';
};

const parseDurationToMinutes = (value?: string | null) => {
  if (!value) return 0;

  const [hours = 0, minutes = 0] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

const formatDuration = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

const compareText = (left?: string | number | null, right?: string | number | null) => {
  return String(left ?? '').localeCompare(String(right ?? ''), 'pt-BR', {
    numeric: true,
    sensitivity: 'base',
  });
};

const getOrderedBillings = (billings: ViagemBilling[]) => {
  return [...billings].sort((left, right) => {
    const dateComparison =
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();

    return dateComparison || right.id - left.id;
  });
};

const getOrderedBillingItems = (billing: ViagemBilling) => {
  return [...(billing.billingItems ?? [])].sort((left, right) => {
    const leftDate = left.viagem?.agendamento
      ? new Date(left.viagem.agendamento).getTime()
      : 0;
    const rightDate = right.viagem?.agendamento
      ? new Date(right.viagem.agendamento).getTime()
      : 0;

    return (
      rightDate - leftDate ||
      compareText(right.viagem?.idViagem, left.viagem?.idViagem)
    );
  });
};

const getExportFileName = (
  reportType: 'TXT' | 'XLSX' | 'CAPA-FATURA-XLSX',
  extension: 'txt' | 'xlsx',
  timestamp: number
) => {
  return `RELAT-${reportType}-${timestamp}.${extension}`;
};

const saveBlob = (content: BlobPart, type: string, fileName: string) => {
  FileSaver.saveAs(new Blob([content], { type }), fileName);
};

const saveWorkbook = (sheets: Record<string, any[]>, fileName: string) => {
  const workbook = XLSX.utils.book_new();

  Object.entries(sheets).forEach(([sheetName, rows]) => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  saveBlob(
    buffer,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    fileName
  );
};

const toBase64 = (arrayBuffer: ArrayBuffer) => {
  let binary = '';
  const bytes = new Uint8Array(arrayBuffer);

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return window.btoa(binary);
};

const fetchImageBase64 = async (src: string) => {
  const response = await fetch(src);
  const blob = await response.blob();
  const buffer = await blob.arrayBuffer();

  return `data:${blob.type};base64,${toBase64(buffer)}`;
};

const getViagemIds = (billing: ViagemBilling) => {
  return (
    billing.billingItems
      ?.map((item) => item.viagem?.idViagem)
      .filter(Boolean)
      .join(', ') || ''
  );
};

const getViagemEstadiaValues = (billing: ViagemBilling) => {
  return getOrderedBillingItems(billing)
    .map((item) => {
      const idViagem = item.viagem?.idViagem || '---';
      const valorEstadia = formatViagemCurrency(item.viagem?.valorEstadia, '');

      return valorEstadia ? `${idViagem}: ${valorEstadia}` : '';
    })
    .filter(Boolean)
    .join('\n');
};

const getBillingTotalValue = (billing: ViagemBilling) => {
  if (billing.totalValue !== null && billing.totalValue !== undefined) {
    return billing.totalValue;
  }

  const totalFromItems = getOrderedBillingItems(billing).reduce(
    (total, item) =>
      total + parseViagemCurrencyValue(item.viagem?.valorEstadia),
    0
  );

  return totalFromItems || null;
};

const getBillingTotalHours = (billing: ViagemBilling) =>
  formatDuration(
    getOrderedBillingItems(billing).reduce(
      (total, item) =>
        total + parseDurationToMinutes(item.viagem?.estadiaPagar),
      0
    )
  );

const buildReportRow = (
  billing: ViagemBilling,
  item?: NonNullable<ViagemBilling['billingItems']>[number],
  itemsLength?: number
) => ({
  'Fatura ID': billing.id,
  'Transportadora CNPJ': billing.carrier?.document || '',
  Transportadora: billing.carrier?.tradeName || '',
  'Data de Emissão': formatDateOnly(billing.createdAt),
  'Criado por': billing.user?.name || '',
  'Quantidade de viagens': billing.totalItems || itemsLength || 0,
  'Viagem ID': item?.viagem?.idViagem || '',
  Protocolo: billing.approvalProtocol || '',
  Shipment: item?.viagem?.shipment || '',
  'CT-e Cobrança': item?.viagem?.conhecimento || '',
  Placa: item?.viagem?.placa || '',
  'Viagem status': item?.viagem?.status || '',
  'Viagem agendamento': formatDateOnly(item?.viagem?.agendamento),
  'Viagem chegada': formatDate(item?.viagem?.chegada),
  'Fim descarregamento': formatDate(item?.viagem?.fimDescarregamento),
  'Fim carregamento': formatDate(item?.viagem?.fimCarregamento),
  'Viagem saída': formatDate(item?.viagem?.saida),
  'Estadia pagar': item?.viagem?.estadiaPagar || '',
  'Valor estadia': formatViagemCurrency(item?.viagem?.valorEstadia, ''),
  Horas: getBillingTotalHours(billing),
  'Valor total da fatura': formatViagemCurrency(
    getBillingTotalValue(billing),
    ''
  ),
});

const buildReportRows = (billings: ViagemBilling[]) => {
  return getOrderedBillings(billings).flatMap((billing) => {
    const items = getOrderedBillingItems(billing);

    if (!items.length) {
      return [buildReportRow(billing)];
    }

    return items.map((item) => buildReportRow(billing, item, items.length));
  });
};

const buildCoverRows = (billings: ViagemBilling[]) => {
  return getOrderedBillings(billings).map((billing) => ({
    'Fatura - Transp': billing.id,
    Transportadora: billing.carrier?.tradeName || '',
    Vendor: billing.carrier?.document || '',
    'Negocio - Site': 'Corteva',
    // Vencimento: '',
    Viagens: getViagemIds(billing),
    'Valor estadias': getViagemEstadiaValues(billing),
    Horas: getBillingTotalHours(billing),
    'Valor total': formatViagemCurrency(getBillingTotalValue(billing), ''),
  }));
};

const thinBorder: Partial<ExcelJS.Borders> = {
  top: { style: 'thin' },
  left: { style: 'thin' },
  bottom: { style: 'thin' },
  right: { style: 'thin' },
};

const noBorder: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: 'FFFFFF' } },
  left: { style: 'thin', color: { argb: 'FFFFFF' } },
  bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
  right: { style: 'thin', color: { argb: 'FFFFFF' } },
};

const styleRange = (
  worksheet: ExcelJS.Worksheet,
  fromRow: number,
  toRow: number,
  fromColumn: number,
  toColumn: number,
  callback: (cell: ExcelJS.Cell) => void
) => {
  for (let row = fromRow; row <= toRow; row += 1) {
    for (let column = fromColumn; column <= toColumn; column += 1) {
      callback(worksheet.getCell(row, column));
    }
  }
};

const buildBillingCoverWorkbook = async (
  billings: ViagemBilling[],
  username?: string
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Protocolo');
  const today = formatDateOnly(new Date().toISOString());
  const now = formatDate(new Date().toISOString());
  const coverRows = buildCoverRows(billings);

  worksheet.columns = [
    { key: 'A', width: 22 },
    { key: 'B', width: 18 },
    { key: 'C', width: 16 },
    { key: 'D', width: 42 },
    { key: 'E', width: 24 },
    { key: 'F', width: 32 },
    { key: 'G', width: 18 },
    { key: 'H', width: 10 },
  ];

  worksheet.mergeCells('A1:B4');
  worksheet.mergeCells('C1:H4');
  worksheet.getCell('C3').value = 'P R O T O C O L O     I N T E R N O';
  worksheet.getCell('C3').font = { bold: true, size: 12 };
  worksheet.getCell('C3').alignment = { horizontal: 'center', vertical: 'middle' };

  try {
    const logoBase64 = await fetchImageBase64(cortevaLogo);
    const imageId = workbook.addImage({
      base64: logoBase64,
      extension: 'png',
    });
    worksheet.addImage(imageId, {
      tl: { col: 0.2, row: 0.7 },
      ext: { width: 190, height: 70 },
    });
  } catch {
    worksheet.getCell('A2').value = 'CORTEVA';
    worksheet.getCell('A2').font = { bold: true, size: 22, color: { argb: 'FF0B6B35' } };
    worksheet.getCell('A2').alignment = { horizontal: 'center', vertical: 'middle' };
  }

  styleRange(worksheet, 1, 5, 1, 8, (cell) => {
    cell.border = noBorder;
  });

  const infoRows = [
    { row: 6, label: 'DE:', value: 'CONTROL TOWER:' },
    { row: 8, label: 'PARA:', value: 'Núcleo Operacional - Contas a Pagar (CAP)' },
    { row: 10, label: 'ASSUNTO:', value: 'Faturas a pagar' },
    { row: 12, label: 'DIA DE PROCESSAMENTO', value: now },
  ];

  infoRows.forEach(({ row, label, value }) => {
    worksheet.mergeCells(`B${row}:F${row}`);
    worksheet.getCell(`A${row}`).value = label;
    worksheet.getCell(`B${row}`).value = value;
    worksheet.getCell(`A${row}`).font = { bold: true };
    worksheet.getCell(`B${row}`).font = { bold: true };
    worksheet.getCell(`B${row}`).alignment = { horizontal: 'center', vertical: 'middle' };
  });

  styleRange(worksheet, 6, 12, 1, 8, (cell) => {
    cell.border = thinBorder;
    cell.font = { ...(cell.font || {}), size: 10 };
  });

  const headers = [
    'Fatura - Transp',
    'Transportadora',
    'Vendor',
    'Negócio - Site',
    // 'Vencimento',
    'Viagens',
    'Valor estadias',
    'Horas',
    'Valor total',
  ];

  headers.forEach((header, index) => {
    const cell = worksheet.getCell(13, index + 1);
    cell.value = header;
    cell.font = { bold: true };
    cell.border = thinBorder;
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  });

  coverRows.forEach((row, index) => {
    const rowNumber = 14 + index;
    const values = [
      row['Fatura - Transp'],
      row.Transportadora,
      row.Vendor,
      row['Negocio - Site'],
      // row.Vencimento,
      row.Viagens,
      row['Valor estadias'],
      row.Horas,
      row['Valor total'],
    ];

    values.forEach((value, columnIndex) => {
      const cell = worksheet.getCell(rowNumber, columnIndex + 1);
      cell.value = value;
      cell.border = thinBorder;
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    });

    const estadiaValuesLines = row['Valor estadias']
      ? row['Valor estadias'].split('\n').length
      : 1;
    worksheet.getRow(rowNumber).height = Math.max(20, estadiaValuesLines * 16);
  });

  const footerRow = 14 + coverRows.length;
  worksheet.getCell(`A${footerRow}`).value = 'OBS:';
  worksheet.getCell(`A${footerRow}`).font = { bold: true };

  const signatureRow = footerRow + 4;
  worksheet.getCell(`A${signatureRow}`).value = 'Enviado por:';
  worksheet.getCell(`A${signatureRow}`).font = { bold: true, italic: true };
  worksheet.getCell(`A${signatureRow + 1}`).value = username || '';
  worksheet.getCell(`A${signatureRow + 2}`).value = 'Nome legível';
  worksheet.getCell(`A${signatureRow + 2}`).font = { bold: true, italic: true };

  worksheet.getCell(`D${signatureRow + 1}`).value = today;
  worksheet.getCell(`D${signatureRow + 1}`).font = { bold: true };
  worksheet.getCell(`D${signatureRow + 1}`).alignment = { horizontal: 'center' };
  worksheet.getCell(`D${signatureRow + 2}`).value = 'Data';
  worksheet.getCell(`D${signatureRow + 2}`).font = { bold: true };
  worksheet.getCell(`D${signatureRow + 2}`).alignment = { horizontal: 'center' };

  worksheet.getCell(`F${signatureRow}`).value = 'Recebido por:';
  worksheet.getCell(`F${signatureRow}`).font = { bold: true, italic: true };
  worksheet.getCell(`F${signatureRow + 2}`).value = 'Nome legível';
  worksheet.getCell(`F${signatureRow + 2}`).font = { bold: true, italic: true };

  ['A', 'D', 'F'].forEach((column) => {
    worksheet.getCell(`${column}${signatureRow + 1}`).border = {
      bottom: { style: 'thin' },
    };
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return buffer;
};

const buildTxt = (billings: ViagemBilling[]) => {
  const formatCell = (
    value: string | number | null | undefined,
    width: number,
    align: 'left' | 'right'
  ) => {
    const text = String(value ?? '').slice(0, width);
    return align === 'right' ? text.padStart(width, ' ') : text.padEnd(width, ' ');
  };

  const formatBillingLine = (billing: ViagemBilling, itemsLength: number) => {
    return [
      'FT',
      formatCell(String(billing.id).padStart(3, '0'), 6, 'left'),
      formatCell(billing.carrier?.document, 18, 'left'),
      formatCell(billing.carrier?.tradeName, 30, 'left'),
      formatCell(formatDateOnly(billing.createdAt), 10, 'left'),
      formatCell(billing.user?.name, 18, 'left'),
      formatCell(itemsLength, 3, 'right'),
      formatCell(
        formatViagemCurrency(getBillingTotalValue(billing), ''),
        14,
        'right'
      ),
    ].join(' ');
  };

  const formatViagemLine = (
    item: NonNullable<ViagemBilling['billingItems']>[number]
  ) => {
    return [
      'VG',
      formatCell(item.viagem?.idViagem, 14, 'left'),
      formatCell(item.viagem?.shipment, 14, 'left'),
      formatCell(item.viagem?.placa, 8, 'left'),
      formatCell(item.viagem?.status, 26, 'left'),
      formatCell(formatDateOnly(item.viagem?.agendamento), 10, 'left'),
      formatCell(formatDate(item.viagem?.chegada), 16, 'left'),
      formatCell(item.viagem?.estadiaPagar, 10, 'right'),
      formatCell(
        formatViagemCurrency(item.viagem?.valorEstadia, ''),
        14,
        'right'
      ),
    ].join(' ');
  };

  const formatTimingLine = (
    item: NonNullable<ViagemBilling['billingItems']>[number]
  ) => {
    return [
      'TM',
      formatCell('FIM_DESC', 8, 'left'),
      formatCell(formatDate(item.viagem?.fimDescarregamento), 16, 'left'),
      formatCell('FIM_CARG', 8, 'left'),
      formatCell(formatDate(item.viagem?.fimCarregamento), 16, 'left'),
      formatCell('SAIDA', 5, 'left'),
      formatCell(formatDate(item.viagem?.saida), 16, 'left'),
    ].join(' ');
  };

  const rows = getOrderedBillings(billings).flatMap((billing) => {
    const items = getOrderedBillingItems(billing);

    if (!items.length) {
      return [formatBillingLine(billing, 0)];
    }

    return [
      formatBillingLine(billing, items.length),
      ...items.flatMap((item) => [formatViagemLine(item), formatTimingLine(item)]),
    ];
  });

  return rows.join('\r\n');
};

export const exportViagensBillings = async (
  billings: ViagemBilling[],
  types: ViagensBillingExportType[],
  username?: string
) => {
  const timestamp = new Date().getTime();

  if (types.includes('txt')) {
    saveBlob(
      buildTxt(billings),
      'text/plain;charset=utf-8',
      getExportFileName('TXT', 'txt', timestamp)
    );
  }

  if (types.includes('xlsx')) {
    saveWorkbook(
      {
        Faturas: buildReportRows(billings),
      },
      getExportFileName('XLSX', 'xlsx', timestamp)
    );
  }

  if (types.includes('billingCover')) {
    const buffer = await buildBillingCoverWorkbook(billings, username);
    saveBlob(
      buffer,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      getExportFileName('CAPA-FATURA-XLSX', 'xlsx', timestamp)
    );
  }
};
